import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  Paper,
  TextField
} from '@mui/material';
import ApiIcon from '@mui/icons-material/Api';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import FeaturesIcon from '@mui/icons-material/Extension';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import PaletteIcon from '@mui/icons-material/Palette';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  
  const tools = [
    {
      title: 'License Management',
      description: 'Manage evaluation and developer licenses',
      icon: <VpnKeyIcon fontSize="large" />,
      path: '/license-upgrade'
    },
    {
      title: 'API Testing',
      description: 'Test API endpoints and view responses',
      icon: <ApiIcon fontSize="large" />,
      path: '/developers/api-test'
    },
    {
      title: 'Tenant Management',
      description: 'Manage tenants and their feature flags',
      icon: <PeopleIcon fontSize="large" />,
      path: '/developers/tenant-management'
    },
    {
      title: 'Tenant Provisioning',
      description: 'Create and provision new tenants',
      icon: <BusinessIcon fontSize="large" />,
      path: '/developers/tenant-provisioning'
    },
    {
      title: 'Feature Flags',
      description: 'Register and manage feature flags and modules (Available with evaluation license)',
      icon: <FeaturesIcon fontSize="large" />,
      path: '/developers/feature-flags'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Developer Tools
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Welcome to the developer dashboard. Here you can access various tools and utilities to help with development, testing, and configuration of the system.
        </Typography>
      </Paper>
      
      <Grid container spacing={3}>
        {tools.map((tool) => (
          <Grid item xs={12} sm={6} md={3} key={tool.title}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, color: 'primary.main' }}>
                  {tool.icon}
                </Box>
                <Typography variant="h6" component="h2" align="center" gutterBottom>
                  {tool.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  {tool.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  fullWidth
                  onClick={() => navigate(tool.path)}
                >
                  Open
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Developer Resources
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button 
              variant="outlined" 
              fullWidth
              startIcon={<CodeIcon />}
              onClick={() => window.open('https://github.com/your-org/your-repo', '_blank')}
            >
              GitHub Repository
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button 
              variant="outlined" 
              fullWidth
              startIcon={<SettingsIcon />}
              onClick={() => window.open('https://your-docs-site.com', '_blank')}
            >
              API Documentation
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button 
              variant="outlined" 
              fullWidth
              startIcon={<FeaturesIcon />}
              onClick={() => navigate('/developers/feature-flags')}
            >
              Feature Flag Registry
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default DeveloperDashboard; 